import { useState } from "react";
import { ArrowLeft, MoveUpRight, ArrowRight, MoveUpLeft } from "lucide-react";
import { useLocale } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';

export default function MonumentCard({ item }: any) {
  const locale = useLocale();
  const { getLocalizedField } = useLocalizedField();
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const allImages = [ ...(item.images || []).slice(0,5)];
  
  const name = getLocalizedField(item, 'name', item.name || 'Unknown');
  const categories = getLocalizedField(item, 'categories', item.categories || '');
  const destinationName = item.destination ? getLocalizedField(item.destination, 'name', item.destination.name || '') : '';

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  return (
    <div 
      className={`
        w-[350px]
        aspect-square
        relative
        bg-primary/20
        rounded-[12px]
        before:absolute
        before:inset-0
        before:pointer-events-none
        before:rounded-[12px]
        before:content-['']
        before:bg-no-repeat
        ${locale === 'ar' 
          ? 'before:bg-[radial-gradient(circle_at_right_top,transparent_12px,theme(colors.background)_12px)] before:bg-[calc(0%_+_(-120px))_calc(100%_-_20px)]' 
          : 'before:bg-[radial-gradient(circle_at_left_top,transparent_12px,theme(colors.background)_12px)] before:bg-[calc(100%_-_(-120px))_calc(100%_-_20px)]'
        }
        before:bg-[length:calc(12px+12px)_calc(12px+4px)]
        after:absolute
        after:inset-0
        after:pointer-events-none
        after:rounded-[12px]
        after:content-['']
        after:bg-no-repeat
        ${locale === 'ar'
          ? 'after:bg-[radial-gradient(circle_at_right_top,transparent_12px,theme(colors.background)_12px)] after:bg-[calc(0%_+_40px)_calc(100%_-_(-20px))]'
          : 'after:bg-[radial-gradient(circle_at_right_top,transparent_12px,theme(colors.background)_12px)] after:bg-[calc(100%_-_30px)_calc(100%_-_(-20px))]'
        }
        after:bg-[length:calc(12px+8px)_calc(12px+20px)]
      `}
    >
      {/* Inner circle button */}
      <div className={`
        absolute
        top-[calc(100%-44px)]
        ${locale === 'ar' ? 'right-[calc(100%-42px)]' : 'left-[calc(100%-42px)]'}
        w-10
        h-10
        rounded-[12px]
        outline-8
        outline-background
        text-center
        leading-10
        z-10
      `}>
        <a href={`/${locale}/monuments/${item.slug}`} className="
          w-full
          h-full
          cursor-pointer
          text-[1.2em]
          bg-transparent
          border-none
          p-0
          [color:transparent]
          [text-shadow:0_0_0_black]
        ">
{locale === 'ar' ? (
  <MoveUpLeft className="mt-2 text-primary mr-2" />
) : (
  <MoveUpRight className="mt-2 text-primary ml-2" />
)}        </a>
      </div>

      {/* Main image carousel */}
      <div className="
        absolute
        top-[20px]
        left-[20px]
        right-[20px]
        h-[180px]
        overflow-hidden
        rounded-[12px]
        group
      ">
        {/* Carousel container */}
        <div 
          className="flex h-full transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(${locale === 'ar' ? currentImageIndex * 100 : -currentImageIndex * 100}%)` }}
        >
          {allImages.map((image, index) => (
            <div 
              key={index}
              className="w-full h-full flex-shrink-0 relative"
            >
              <img
                src={image}
                alt={`${name} - Image ${index + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Vignette */}
              { item.vignette && (
                <div className="
                  absolute
                  right-[12px]
                  top-[12px]
                  h-12
                  w-12
                  overflow-hidden
                  rounded-lg
                  border-2
                  border-background/50
                  shadow-lg
                  z-10
                ">
                  <img
                    src={item.vignette}
                    alt={`${name} vignette`}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation arrows (show on hover) */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className={`
                absolute
                ${locale === 'ar' ? 'right-2' : 'left-2'}
                top-1/2
                transform
                -translate-y-1/2
                w-8
                h-8
                rounded-full
                bg-black/30
                backdrop-blur-sm
                flex
                items-center
                justify-center
                opacity-0
                group-hover:opacity-100
                transition-opacity
                duration-200
                text-white
                z-20
                hover:bg-black/50
              `}
            >
              {locale === 'ar' ? (
                <ArrowRight size={16} />
              ) : (
                <ArrowLeft size={16} className="ml-[-5px]" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className={`
                absolute
                ${locale === 'ar' ? 'left-2' : 'right-2'}
                top-1/2
                transform
                -translate-y-1/2
                w-8
                h-8
                rounded-full
                bg-black/30
                backdrop-blur-sm
                flex
                items-center
                justify-center
                opacity-0
                group-hover:opacity-100
                transition-opacity
                duration-200
                text-white
                z-20
                hover:bg-black/50
              `}
            >
              {locale === 'ar' ? (
                <ArrowLeft size={16} className="ml-[-5px]" />
              ) : (
                <ArrowRight size={16} />
              )}
            </button>
          </>
        )}

        {/* Dots indicator (positioned inside the image area) */}
        {allImages.length > 1 && (
          <div className="
            absolute
            bottom-3
            left-1/2
            transform
            -translate-x-1/2
            flex
            items-center
            gap-1.5
            z-10
          ">
            {allImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                className={`
                  w-2
                  h-2
                  rounded-full
                  transition-all
                  duration-200
                  ${index === currentImageIndex 
                    ? 'bg-white w-4' 
                    : 'bg-white/60 hover:bg-white/80'
                  }
                `}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content section */}
      <section className="
        absolute
        top-[220px]
        left-[36px]
        right-[36px]
      ">
        {/* Title */}
        <h2 className="
          text-[1.3em]
          font-semibold
          mb-1
          line-clamp-1
        "
        title={name}
        >
          {name}
        </h2>
        
        {/* Destination */}
        {destinationName && (
          <p className="
            text-sm
            text-muted-foreground
            mb-4
          ">
            {destinationName}
          </p>
        )}
        
        {/* Category */}
        {categories && (
          <span className="
            inline-block
            rounded-full
            bg-primary/50
            px-3
            py-1
            text-xs
            font-medium
            text-black
            mb-4
          ">
            {categories}
          </span>
        )}
      </section>
    </div>
  );
}